import React, { useCallback, useEffect, useState } from 'react';
import { architectAPI } from '../api';
import { toast } from 'sonner';
import { CheckCircle2, Eye, Lock, Rocket, Send, RotateCcw } from 'lucide-react';

const STAGES = [
  ['DESIGN', 'Design'],
  ['INTERNAL_REVIEW', 'Internal Review'],
  ['CLIENT_REVIEW', 'Client Review'],
  ['APPROVED', 'Approved'],
  ['IFC', 'IFC']
];

const LABEL_STYLE = {
  CURRENT_WORKING: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
  LAST_SUBMITTED: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200',
  INTERNALLY_APPROVED: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  CLIENT_APPROVED_LOCKED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
  IFC_RELEASED: 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900',
  SUPERSEDED: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
};

export const ArchitectWorkflowPanel = ({ id, delId, onChange }) => {
  const [wf, setWf] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    architectAPI.deliverableWorkflow(id, delId).then(r => setWf(r.data)).catch(() => setWf(null));
  }, [id, delId]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); load(); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  if (!wf) return null;
  const can = wf.can || {};
  const stageIdx = STAGES.findIndex(([k]) => k === wf.stage);

  const review = (rev, decision) => {
    const comments = decision === 'REVISION_REQUIRED'
      ? window.prompt(`Return ${rev.revision} for revision — what must change?`)
      : window.prompt(`Approve ${rev.revision} internally. Review note (optional):`) || '';
    if (decision === 'REVISION_REQUIRED' && !comments) return;
    run(() => architectAPI.performReview(id, delId, rev.id, { decision, comments }),
      decision === 'INTERNAL_APPROVED' ? `${rev.revision} internally approved` : `${rev.revision} returned for revision`);
  };

  const sendToClient = (rev) => run(async () => {
    const r = await architectAPI.requestClientApproval(id, delId, rev.id, {});
    const link = `${window.location.origin}${r.data.approval_path}`;
    try { await navigator.clipboard.writeText(link); } catch { /* clipboard unavailable */ }
    toast.message('Client approval link ready', { description: link });
  }, `${rev.revision} sent for client approval`);

  const recordDecision = (rev) => {
    const decision = window.prompt(`Record the client decision for ${rev.revision}: APPROVED / APPROVED_WITH_COMMENTS / CHANGES_REQUESTED`);
    if (!decision) return;
    const comments = window.prompt('Client comments (required for changes requested):') || '';
    run(() => architectAPI.recordClientDecision(id, delId, rev.id, {
      decision: decision.trim().toUpperCase(), comments, client_name: 'Client', source: 'RECORDED_BY_CRM'
    }), `Client decision recorded on ${rev.revision}`);
  };

  const release = (rev) => {
    const note = window.prompt(`Release ${rev.revision} as IFC (Issued For Construction)? Release note:`);
    if (note === null) return;
    run(() => architectAPI.releaseIfc(id, delId, rev.id, { note }), `${rev.revision} released as IFC`);
  };

  return (
    <div className="mt-3 rounded border bg-slate-50 p-3 dark:bg-slate-900/40" data-testid={`workflow-panel-${delId}`}>
      <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="workflow-strip">
        {STAGES.map(([key, label], i) => (
          <React.Fragment key={key}>
            <span data-testid={`workflow-stage-${key}`}
              className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${i <= stageIdx
                ? 'bg-primary text-white' : 'bg-white text-muted-foreground dark:bg-slate-800'}`}>
              {label}
            </span>
            {i < STAGES.length - 1 && <span className="text-[11px] text-muted-foreground">→</span>}
          </React.Fragment>
        ))}
      </div>

      <div className="mb-3 grid gap-2 text-[11px] sm:grid-cols-2 lg:grid-cols-4">
        <p data-testid="wf-working">Working revision: <b>{wf.current_revision || '—'}</b></p>
        <p data-testid="wf-internal">Internally approved: <b>{wf.internal_approved_revision || '—'}</b></p>
        <p data-testid="wf-client">Client approved: <b>{wf.client_approved_revision || '—'}</b></p>
        <p data-testid="wf-ifc">IFC revision: <b>{wf.ifc_revision || '—'}</b></p>
      </div>
      <p className="mb-3 text-xs" data-testid="wf-pending">
        Pending action: <b>{wf.pending_action}</b>
        {wf.due_date ? <span className="text-muted-foreground"> · due {String(wf.due_date).slice(0, 10)}</span> : null}
      </p>
      {wf.open_revision_request && (
        <p className="mb-3 rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
          data-testid="wf-open-request">
          Open {wf.open_revision_request.source === 'CLIENT' ? 'client' : 'internal'} revision request: {wf.open_revision_request.description}
        </p>
      )}

      <ul className="space-y-2">
        {wf.revisions.map(rev => (
          <li key={rev.id} className="rounded border bg-white p-2 dark:bg-slate-900" data-testid={`wf-rev-${rev.revision}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex flex-wrap items-center gap-1.5 text-xs">
                <b>{rev.revision}</b>
                {rev.locked && <Lock className="h-3 w-3 text-emerald-600" />}
                {rev.labels.map(l => (
                  <span key={l} className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${LABEL_STYLE[l] || 'bg-slate-100'}`}
                    data-testid={`wf-label-${rev.revision}-${l}`}>{l.replaceAll('_', ' ')}</span>
                ))}
                <span className="text-muted-foreground">· review {rev.review_status} · client {rev.client_status} · {rev.ifc_status}</span>
              </span>
              <span className="flex flex-wrap items-center gap-1.5">
                {can.submit_review && ['DRAFT', 'REVISION_REQUIRED'].includes(rev.review_status) && !rev.locked && (
                  <button disabled={busy} data-testid={`wf-submit-${rev.revision}`}
                    onClick={() => run(() => architectAPI.submitForReview(id, delId, rev.id, {}), `${rev.revision} submitted for internal review`)}
                    className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] disabled:opacity-40">
                    <Send className="h-3 w-3" />Submit for review
                  </button>
                )}
                {can.perform_review && rev.review_status === 'IN_REVIEW' && (
                  <>
                    <button disabled={busy} data-testid={`wf-approve-${rev.revision}`} onClick={() => review(rev, 'INTERNAL_APPROVED')}
                      className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] text-emerald-700 disabled:opacity-40">
                      <CheckCircle2 className="h-3 w-3" />Internal approve
                    </button>
                    <button disabled={busy} data-testid={`wf-revise-${rev.revision}`} onClick={() => review(rev, 'REVISION_REQUIRED')}
                      className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] text-amber-700 disabled:opacity-40">
                      <RotateCcw className="h-3 w-3" />Revision required
                    </button>
                  </>
                )}
                {can.request_client_approval && rev.review_status === 'INTERNAL_APPROVED' && rev.client_status === 'NOT_SENT' && (
                  <button disabled={busy} data-testid={`wf-send-client-${rev.revision}`} onClick={() => sendToClient(rev)}
                    className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] disabled:opacity-40">
                    <Eye className="h-3 w-3" />Send to client
                  </button>
                )}
                {can.record_client_decision && rev.client_status === 'SENT' && (
                  <button disabled={busy} data-testid={`wf-record-${rev.revision}`} onClick={() => recordDecision(rev)}
                    className="rounded border px-2 py-0.5 text-[11px] disabled:opacity-40">Record client decision</button>
                )}
                {can.release_ifc && rev.client_status?.startsWith('APPROVED') && rev.ifc_status !== 'RELEASED' && (
                  <button disabled={busy} data-testid={`wf-release-${rev.revision}`} onClick={() => release(rev)}
                    className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] text-slate-900 dark:text-slate-100 disabled:opacity-40">
                    <Rocket className="h-3 w-3" />Release as IFC
                  </button>
                )}
              </span>
            </div>
            {(rev.review_comments || []).length > 0 && (
              <ul className="mt-1.5 space-y-0.5" data-testid={`wf-comments-${rev.revision}`}>
                {rev.review_comments.map((c, i) => (
                  <li key={i} className="text-[11px] text-muted-foreground">
                    {c.reviewer_name} · {c.decision} · {c.comment}
                  </li>
                ))}
              </ul>
            )}
            {rev.client_comments && (
              <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`wf-client-comment-${rev.revision}`}>
                Client ({rev.client_decided_by_label}): {rev.client_comments}
              </p>
            )}
            {rev.released_at && (
              <p className="mt-1 text-[11px] font-medium" data-testid={`wf-released-${rev.revision}`}>
                IFC / RELEASED FOR EXECUTION · {rev.released_by_name} · {new Date(rev.released_at).toLocaleString()}
              </p>
            )}
          </li>
        ))}
        {wf.revisions.length === 0 && <li className="text-xs text-muted-foreground">Upload a drawing revision to start the approval workflow.</li>}
      </ul>
    </div>
  );
};
